import { ReactNode } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormTrigger,
} from 'react-hook-form';
import { ZodSchema } from 'zod';
import StepItemWrapper from './shared-toolbar-form-stepper-item';

import Stepper from '@mui/material/Stepper';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { SvgIconProps } from '@mui/material/SvgIcon';
export interface ActionTypeProps {
  id: string;
  label: string;
}

export interface FormSubSteps {
  stepIndex: string;
  title: string;
  description?: string;
  hasError: boolean;
}

export interface ActionErrorEntry {
  id: string;
}

export interface StepConfig {
  id?: string;
  label: string;
  header: string;
  subheader?: string;
  schema?: ZodSchema;
  description?: string;
  actionTypes?: ActionTypeProps[];
  icon?: React.ElementType<SvgIconProps>;
  component?: ReactNode;
}

export interface SharedToolbarFormStepperProps<T extends FieldValues> {
  activeStep: number;
  setActiveStep: (step: number) => void;
  steps: StepConfig[];
  errors: FieldErrors<T>;
  trigger: UseFormTrigger<T>;
  stepIsLocked: boolean[];
  handleStepClick?: (index: number) => void;
  handleValidation?: () => boolean;
  subSteps?: FormSubSteps[];
  control: Control<T>;
  getValues: UseFormGetValues<T>;
}

export default function SharedToolbarFormStepper<T extends FieldValues>({
  activeStep,
  setActiveStep,
  steps,
  errors,
  stepIsLocked,
  handleStepClick,
  subSteps,
  control,
  getValues,
}: SharedToolbarFormStepperProps<T>) {
  const theme = useTheme();

  async function onStepClick(stepIndex: number) {
    const targetId = steps[stepIndex].id;

    if (targetId) {
      requestAnimationFrame(() => {
        document.getElementById(targetId)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    if (stepIndex === activeStep || stepIsLocked[stepIndex]) {
      return;
    }
    setActiveStep(stepIndex);
  }

  return (
    <Box sx={{ flexGrow: 1, pr: 1, ml: 1 }}>
      <Stepper
        nonLinear
        activeStep={activeStep}
        orientation="horizontal"
        sx={{
          '& .MuiStepLabel-iconContainer .MuiStepIcon-root': {
            fontSize: 10,
            borderRadius: '50%',
            opacity: 1,
          },
          '& .MuiStepLabel-iconContainer': {
            pr: '6px',
          },
          '& .MuiStepIcon-root.Mui-active': {
            backgroundColor: theme.palette.semantic.stepper.active,
          },
          '& .MuiStepIcon-root.Mui-completed': {
            color: theme.palette.semantic.stepper.completed,
          },
          '& .MuiStepContent-root': {
            pl: 2.5,
            ml: 0.5,
          },
          '& .MuiStepIcon-text': { display: 'none' },
          '& .MuiStepConnector-root': { ml: 0.5 },
          '& .MuiStepConnector-line': { minHeight: 10 },
        }}
      >
        {steps.map((step, index) => {
          return (
            <StepItemWrapper
              key={step.label}
              step={step}
              index={index}
              activeStep={activeStep}
              stepIsLocked={stepIsLocked}
              errors={errors}
              control={control}
              subSteps={subSteps}
              theme={theme}
              onStepClick={onStepClick}
              handleStepClick={handleStepClick}
              getValues={getValues}
            />
          );
        })}
      </Stepper>
    </Box>
  );
}
